import { readStored } from '@/lib/storage'
import { readLanguage } from '@/lib/language'

/**
 * Köhnə TV mühərrikləri üçün minimal runtime bərabərləşdirmə.
 *
 * ⚠ ƏSAS YANLIŞ TƏSƏVVÜR: `esbuild`/`tsc` yalnız SİNTAKSİSİ endirir
 * (arrow → function, `class` → prototip). RUNTIME API-nı ƏLAVƏ ETMİR.
 * Yəni `[].flat()` və ya `Object.entries()` yazsan, köhnə TV-də kod
 * `undefined is not a function` ilə ÇÖKÜR — və çökən JS-dən sonra ekran
 * boot skeletonunda dondurulmuş qalır. İstifadəçi bunu «app dondu» kimi görür.
 *
 * Həll iki qatlıdır:
 *   1. `tsconfig.json`-da `lib: ES2015` — qadağan API kompilyasiya vaxtı tutulur.
 *   2. Bu fayl — həqiqətən lazım olan bir neçə şeyin özümüzün yazdığı əvəzi.
 *
 * Burada `Array.prototype`-a heç nə əlavə EDİLMİR (prototip pozmaq TV-də
 * başqa app-larla toqquşur) — sadəcə funksiya ixrac olunur.
 */

/*
  ⚠ VENDOR KİTABXANASI ÜÇÜN STATİK POLİFİL (2026-09-26): `dist-web/hls.min.js` (hls.js 1.5)
    `Object.entries`-i 6 yerdə polifilsiz çağırır — Chrome 54+, webOS 4.0 isə Chrome 53-dür.
    Bizim kod yuxarıdakı `entries()` ilə gedir; bu qoruyucu YALNIZ metod yoxdursa əlavə
    olunur və prototipə toxunmur (statik metoddur). hls.js `primeHls()` ilə sonra yüklənir.
*/
type ObjectStatics = {
  entries?: (object: Record<string, unknown>) => Array<[string, unknown]>
  values?: (object: Record<string, unknown>) => unknown[]
}
const objectStatics = Object as unknown as ObjectStatics
if (typeof objectStatics.entries !== 'function') {
  objectStatics.entries = (object) => entries(object)
}
if (typeof objectStatics.values !== 'function') {
  objectStatics.values = (object) => {
    const out: unknown[] = []
    for (const key in object) {
      if (Object.prototype.hasOwnProperty.call(object, key)) out.push(object[key])
    }
    return out
  }
}

/** Chrome 57-dən əvvəl yoxdur. */
export function padStart(value: string, length: number, pad: string): string {
  let out = value
  while (out.length < length) out = pad + out
  return out
}

/** Chrome 54-dən əvvəl yoxdur. */
export function entries<T>(object: Record<string, T>): Array<[string, T]> {
  const out: Array<[string, T]> = []
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) out.push([key, object[key] as T])
  }
  return out
}

/**
 * `requestAnimationFrame` webOS 3-də prefiksli ola bilər. Fallback `setTimeout`
 * DEYİL 16ms-lik `setTimeout` — kadr sərhədinə düşməyən taymer TV-də daha çox
 * kadr düşürür; 32ms (30fps) daha sabitdir və pult naviqasiyası üçün kifayətdir.
 */
type RafHost = {
  requestAnimationFrame?: (cb: FrameRequestCallback) => number
  webkitRequestAnimationFrame?: (cb: FrameRequestCallback) => number
}

/** `requestAnimationFrame` örtüyü — köhnə TV mühərriklərində prefiksli ola bilir. */
export const raf: (cb: FrameRequestCallback) => number = (() => {
  const host = window as unknown as RafHost
  const native = host.requestAnimationFrame || host.webkitRequestAnimationFrame
  if (native) return native.bind(window)
  return (cb: FrameRequestCallback) => window.setTimeout(() => cb(now()), 32)
})()

/** `performance.now()` webOS 3-də olmaya bilər. */
export function now(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

/**
 * Brauzerin HƏQİQİ Fullscreen API-si (köhnə `webkit` prefiksi ilə) — TV
 * app-ının indiyədək YALNIZ CSS ilə (1920×1080 kətanı doldurmaqla) simulyasiya
 * etdiyi «tam ekran»dan fərqli olaraq, bu, brauzer chrome-unu (ünvan çubuğu/
 * tab-lar) da gizlədir. `apps/web/src/components/LiveClient.tsx`-dəki eyni
 * naxış — TV-yə köçürülüb ki, player ekranı açılan kimi çağırıla bilsin.
 */
interface FsDoc extends Document {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
}
interface FsEl extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void
}

/**
 * ⚠ İSTİFADƏÇİ JESTİ TƏLƏB OLUNUR: brauzerlər `requestFullscreen()`-i yalnız
 *   toxunma/klik/Enter-in birbaşa çağırış zəncirində qəbul edir. Player ekranı
 *   HƏMİŞƏ pult/siçan aksiyasından (`navigate(...)`) dərhal yaranır, ona görə
 *   bu şərt adətən ödənilir — amma rədd olunsa (məs. Promise sətri gözlədikdən
 *   sonra çağırılıbsa) sakit-uğursuz olur, player YENƏ DƏ CSS-ölçülü kətanda
 *   normal işləyir (qara ekran/çökmə yoxdur).
 */
export function enterFullscreen(el: HTMLElement): void {
  const e = el as FsEl
  try {
    if (e.requestFullscreen) void e.requestFullscreen().catch(() => undefined)
    else if (e.webkitRequestFullscreen) void e.webkitRequestFullscreen()
  } catch {
    // dəstəklənmir/rədd olundu — CSS-ölçülü kətan yenə işləyir.
  }
}

/**
 * Hazırda brauzerin HƏQİQİ tam ekran rejimindəyikmi.
 *
 * ⚠ CSS-ölçülü «tam ekran» İLƏ QARIŞDIRMA: TV app-ı onsuz da 1920×1080 kətanı
 *   doldurur. Bu funksiya yalnız brauzerin Fullscreen API vəziyyətini deyir —
 *   yəni ünvan çubuğunun gizli olub-olmadığını.
 */
export function isFullscreen(): boolean {
  const d = document as FsDoc
  return !!(document.fullscreenElement || d.webkitFullscreenElement)
}

/** Tam ekrandan çıxır (brauzerlərin fərqli metod adları örtülür). */
export function exitFullscreen(): void {
  const d = document as FsDoc
  try {
    if (document.fullscreenElement && document.exitFullscreen) void document.exitFullscreen().catch(() => undefined)
    else if (d.webkitFullscreenElement && d.webkitExitFullscreen) void d.webkitExitFullscreen()
  } catch {
    // artıq tam ekrandan çıxılıb və ya dəstəklənmir.
  }
}

/**
 * ⚠⚠ TAM EKRANDAN ÇIXIŞA ABUNƏ — BRAUZERİN «ESC»-İ UDMASININ YEGANƏ ƏLİ
 *   (MYTasks 549: «veb tv player esc basanda dönüş etməlidir ilk dəfədən amma
 *   etmir»).
 *
 *   KÖK SƏBƏB: səhifə HƏQİQİ tam ekranda olanda (bax `enterFullscreen`) Esc
 *   brauzerin ÖZ jestidir — Chrome/Firefox onu tam ekranı bağlamağa işlədir və
 *   `keydown`-u SƏHİFƏYƏ ÜMUMİYYƏTLƏ VERMİR (istifadəçini tam ekranda tələyə
 *   salan səhifələrə qarşı qoruma). Yəni player-in `onKey`-i birinci basışı HEÇ
 *   VAXT GÖRMÜR; istifadəçi ikinci dəfə basmalı olur. Pultun Back-i (Tizen
 *   10009 · webOS 461) bu qorumaya düşmür, ona görə qüsur YALNIZ veb TV-də
 *   (masaüstü brauzer) görünürdü — sahibin təsvir etdiyi məhz budur.
 *
 *   HƏLL: tam ekran BİZİM istəyimiz olmadan bağlananda bunu «geri» basılışı
 *   saymaq. Hadisə hər iki adla dinlənir (`webkit` prefiksi köhnə TV/Safari
 *   mühərriklərində).
 *
 * ⚠ ÇAĞIRAN DİNLƏYİCİNİ MÜTLƏQ GÖTÜRMƏLİDİR (qaytarılan funksiya) — `document`-ə
 *   bağlıdır və ekranla birlikdə ölmür.
 */
export function onFullscreenExit(handler: () => void): () => void {
  const listener = (): void => {
    if (!isFullscreen()) handler()
  }
  document.addEventListener('fullscreenchange', listener, false)
  document.addEventListener('webkitfullscreenchange', listener, false)
  return () => {
    document.removeEventListener('fullscreenchange', listener, false)
    document.removeEventListener('webkitfullscreenchange', listener, false)
  }
}

/**
 * `AbortController` Chrome 66+-dır — TV-də etibar etmək olmaz.
 * Ona görə timeout `Promise.race` ilə qurulur: sorğunun özü davam edə bilər,
 * amma UI onu gözləməkdə ilişib qalmır (donmanın bir səbəbi məhz budur —
 * şəbəkə cavabsız qalanda ekran «Yüklənir…»-də dayanır).
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false
    const timer = window.setTimeout(() => {
      if (settled) return
      settled = true
      reject(new Error(`${label}: ${ms}ms vaxt bitdi`))
    }, ms)
    promise.then(
      (value) => {
        if (settled) return
        settled = true
        window.clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        if (settled) return
        settled = true
        window.clearTimeout(timer)
        reject(error instanceof Error ? error : new Error(String(error)))
      },
    )
  })
}

/**
 * Kök-nisbi `/api…` yolunu build vaxtı verilən API origin-inə bağlayır.
 *
 * ⚠ Paketlənmiş/deploy edilmiş TV app-ı API-dən AYRI origin-dən açılır
 * (`file://` və ya `tv.kinobox.az`) və orada `/api`-ni proksiləyən YOXDUR
 * — nisbi `/api` ya fayl sistemində, ya da SPA fallback-ında (HTML qaytarır)
 * axtarılır, `response.json()` çökür və kataloq səssizcə MOCK-a düşür (yalnız
 * list gəlir, şəkil/video yox). `mediaUrl` (images.ts) artıq bu origin-i işlədir;
 * JSON qatı da eyni origin-i işlətməlidir. Dev-də `VITE_KB_API_ORIGIN` boşdur və
 * Vite proxy nisbi `/api`-ni özü həll edir, ona görə yol olduğu kimi qalır.
 */
const API_ORIGIN = (import.meta.env.VITE_KB_API_ORIGIN as string | undefined) || ''

/**
 * Build vaxtı verilən API origin-i (sonundakı «/» olmadan). Boş sətir = dev,
 * yəni Vite proxy nisbi yolu özü həll edir.
 *
 * ⚠ `images.ts` bunu SON FALLBACK kimi işlədir: media origin-i artıq
 * `GET /api/media/config`-dən gəlir, amma həmin sorğu (şəbəkə/timeout) alınmasa
 * paketlənmiş app-da nisbi yol `file://`-də axtarılar və şəkillər ÜMUMİYYƏTLƏ
 * görünməz. API origin-i ən azı serverin öz disk fayllarını verir.
 */
export function apiOrigin(): string {
  return API_ORIGIN.replace(/\/+$/, '')
}

/**
 * API ünvanını qurur.
 * ⚠ Paketlənmiş TV app-ı `file://`-dən açılır — nisbi yol orada TV-nin fayl
 * sistemində axtarılır və 404 verir; ona görə mütləq ünvan lazımdır.
 */
export function apiUrl(path: string): string {
  if (!path || path.charAt(0) !== '/') return path
  if (!API_ORIGIN) return path
  return API_ORIGIN.replace(/\/+$/, '') + path
}

/**
 * Status kodu MƏLUMAT DAŞIYAN sorğular üçün (QR poll: 202 gözləyir · 200 token ·
 * 410 vaxtı keçib). Cavabı ATMIR — status + mətn qaytarır, çağıran özü qərar verir.
 *
 * ⚠ NİYƏ LAZIM OLDU (2026-07-29): `session.ts`-dəki QR poll-u BİRBAŞA `fetch`
 *   çağırırdı, `typeof fetch` yoxlaması olmadan. `fetch` OLMAYAN TV brauzerində
 *   (Tizen/webOS-un köhnə nəsli — Chrome 38-47) bu, sorğu getməmişdən əvvəl
 *   `ReferenceError` atırdı: QR ekranı «Serverə qoşulmaq alınmadı» yazır, halbuki
 *   şəbəkə tamamilə sağlamdır. PC brauzerində `fetch` var deyə problem görünmürdü —
 *   istifadəçinin təsvir etdiyi fərq məhz budur.
 */
export interface HttpResponse {
  status: number
  text: string
}

/**
 * ⚠⚠ TV-DƏKİ «DONMANIN» KÖK SƏBƏBİ VƏ NİYƏ TRANSPORT XHR-DİR (2026-08-01).
 *
 * Əvvəl bu funksiyanın İKİ gövdəsi vardı: `fetch` varsa fetch, yoxsa XHR.
 * Yalnız XHR budağının vaxt həddi vardı (`xhr.timeout`) — **fetch budağının HEÇ BİR
 * VAXT HƏDDİ YOX İDİ**. Yəni müasir TV brauzerində (fetch var) asılıb qalan sorğu
 * ƏBƏDİ asılı qalırdı. Çağırış yerlərindəki `withTimeout` bunu ÖRTMÜR: o, yalnız
 * promise-i rədd edir, ALTDAKI SOKET İSƏ AÇIQ QALIR (faylın öz şərhi də bunu deyir).
 *
 * Nəticə zəncir reaksiyasıdır: brauzerin bir host-a paralel bağlantı limiti 6-dır.
 * Hər 60 saniyədən bir gedən heartbeat (`playback.ts`) və hər təkrar cəhd asılı bir
 * soket qoyur; ALTI dənədən sonra YENİ sorğuların hamısı növbədə gözləyir və
 * göndərilmir. Ekranda heç nə yüklənmir, pult işləyir — istifadəçi bunu «donma»
 * kimi görür və yalnız cihazı yenidən başladanda düzəlir (soketlər ölür).
 *
 * HƏLL — TƏK TRANSPORT, XHR:
 *   · `xhr.timeout` sorğunu HƏQİQƏTƏN kəsir (soket buraxılır), `fetch` isə bunu
 *     yalnız `AbortController` ilə edir — o da Chrome 66+-dır, TV-də yoxdur;
 *   · iki gövdə = iki davranış; biri düzəldiləndə o biri arxada qalırdı (məhz bu
 *     baş vermişdi). İndi hər sorğu, istisnasız, eyni həddə tabedir.
 *   · XHR bu app-ın dəstəklədiyi HƏR TV brauzerində var (Chrome 38+).
 * fetch-in bizə verəcəyi heç bir üstünlük yoxdur — sorğular sadə JSON-dur.
 */
export function httpStatus(
  url: string,
  init?: { method?: string; body?: string; headers?: Record<string, string>; timeoutMs?: number },
): Promise<HttpResponse> {
  const method = (init && init.method) || 'GET'
  const body = init && init.body
  const extra = (init && init.headers) || {}
  // ⚠ SƏTH QUERY-DƏ (kənar keş `Vary`-ni nəzərə almır — bax `Auth/ClientSurface.cs`).
  var withSurface = apiUrl(url)
  if (withSurface.indexOf('surface=') < 0) {
    withSurface = withSurface + (withSurface.indexOf('?') >= 0 ? '&' : '?') + 'surface=webtv'
  }
  // ⚠⚠ UŞAQ PROFİLİNİN YAŞ HƏDDİ — EYNİ SƏBƏBLƏ QUERY-DƏ (sahib tələbi 2026-08-26).
  //   Kataloq ucları keşlənir və kənar keş `Vary` başlığına baxmır: hədd başlıqla
  //   getsəydi uşağın süzülmüş cavabı böyüklərə, böyüyün süzülməmiş cavabı isə uşağa
  //   verilərdi. Backend izahı: `IViewerAgeLimit`.
  // Saxlanc örtüyü disk xətasında da həddi saxlayır; profiles importu dövrə yaradardı.
  var maxAge = readStored('kb_tv_max_age')
  if (maxAge && withSurface.indexOf('maxAge=') < 0) {
    withSurface = withSurface + (withSurface.indexOf('?') >= 0 ? '&' : '?') + 'maxAge=' + maxAge
  }
  /*
    ⚠⚠ DİL DƏ QUERY-DƏ — VƏ MƏHZ BURADA (2026-08-30).

    ÖLÇÜLDÜ: `lang=` yalnız KATALOQ ucuna əl ilə qoyulurdu (`withLanguage()`), yəni
    `/api/live/*`, `/api/sessions`, `/api/me/*` və gələcək hər yeni uc dil
    seçimindən XƏBƏRSİZ gedirdi — istifadəçi «Русский» seçəndə canlı yayımın
    veriliş adı və seans başlığı azərbaycanca qalırdı. Səbəb struktur idi: dili
    HƏR ÇAĞIRIŞ YERİ özü yazmalı idi, yəni bir yerdə unudulmaq kifayət edirdi.
    Transporta qoyulanda uc DİLƏ GÖRƏ DÜZGÜN OLUR — unutmaq mümkün deyil.

    ⚠ MÖVCUD ÇAĞIRIŞ YERLƏRİ ÜSTÜNDƏ QALIR: `indexOf('lang=') < 0` yoxlaması
      `withLanguage()`-in və əl ilə yazılmış parametrin sözünü SON söz saxlayır
      (məsələn gələcəkdə bir uc qəsdən başqa dildə soruşulsa).
    ⚠ `surface=`/`maxAge=` ilə eyni səbəb: kənar keş `Vary` başlığını nəzərə almır,
      ona görə dil BAŞLIQLA yox, QUERY ilə getməlidir — əks halda rusca cavab
      azərbaycanca soruşan cihaza verilərdi.
    ⚠ SAXLANCDAN YOX, `readLanguage()`-dən OXUNUR (yuxarıdakı `maxAge`-dən fərqli):
      `lib/language.ts` seçimi prosesdə də keşləyir və `localStorage` yazısı ATSA
      (gizli rejim) keş yenə düzgün qalır — yəni bu yol ilə `withLanguage()` HEÇ
      VAXT fərqli dil deyə bilməz. Dövrə yoxdur: həmin modul heç nə import etmir.
  */
  if (withSurface.indexOf('lang=') < 0) {
    withSurface = withSurface + (withSurface.indexOf('?') >= 0 ? '&' : '?') + 'lang=' + readLanguage()
  }

  const target = withSurface

  return new Promise<HttpResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, target, true)
    for (const key in extra) xhr.setRequestHeader(key, extra[key] as string)
    // ⚠ ŞƏKİL VARİANTI ÜÇÜN SƏTH (backend `Auth/ClientSurface.cs`, V056).
    //   Veb TV — Tizen/webOS: `webtv`. Səthə xas kadr yoxdursa server ümumi TV
    //   kadrına, o da yoxdursa hər hansı şəklə düşür (zəncir serverdədir).
    xhr.setRequestHeader('x-kinobox-surface', 'webtv')
    if (body) xhr.setRequestHeader('content-type', 'application/json')
    /**
     * Hədd TRANSPORT-dadır ki, çağırış yeri onu unuda bilməsin.
     *
     * ⚠ ÜSTÜNDƏN YAZMAQ YALNIZ LONG-POLL ÜÇÜNDÜR (MYTasks 577,
     *   `/api/me/age-state/wait`): server cavabı QƏSDƏN 25 saniyəyə qədər
     *   saxlayır, 20 saniyəlik ümumi hədd isə onu HƏR DÖVRƏDƏ «vaxt bitdi»
     *   xətasına salardı — yəni ani sinxron heç vaxt işləməzdi. Sahə opsionaldır
     *   və verilməyəndə davranış ZƏRRƏ QƏDƏR dəyişmir; adi sorğuda ondan
     *   istifadə etmək qadağandır (yuxarıdakı «asılı soket» zəncir reaksiyası).
     */
    xhr.timeout = (init && init.timeoutMs) || NETWORK_TIMEOUT_MS
    xhr.ontimeout = () => reject(new Error(url + ' → vaxt bitdi'))
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText })
    xhr.onerror = () => reject(new Error(url + ' → şəbəkə xətası'))
    xhr.onabort = () => reject(new Error(url + ' → dayandırıldı'))
    xhr.send(body)
  })
}

/**
 * Şəbəkə vaxt həddi — BÜTÜN TV sorğuları üçün TƏK MƏNBƏ.
 *
 * ⚠ 8 SANİYƏ AZ İDİ (2026-07-29). TV brauzerində ilk sorğu DNS + TLS əl-sıxması +
 *   soyuq başlanğıcla birlikdə asanlıqla 10 saniyəni keçir — PC-də eyni sorğu
 *   200 ms çəkir. Nəticədə TV «Serverə qoşulmaq alınmadı» yazırdı, halbuki
 *   internet işləyirdi.
 *
 * ⚠ NİYƏ TƏK YERƏ YIĞILDI (2026-07-31): hədd hər faylda AYRICA saxlanılırdı və
 *   dəyərlər SÜRÜŞMÜŞDÜ — `session.ts` 20000-ə qaldırılmışdı, `playback.ts` və
 *   `live.ts` isə hələ 8000-də qalmışdı. Yəni eyni səbəbdən bilet sorğusu vaxtından
 *   əvvəl kəsilib «Oynatma hazır deyil» verə bilirdi. Tək sabit bu sürüşmənin
 *   təkrarlanmasına imkan vermir.
 */
export const NETWORK_TIMEOUT_MS = 20000

/**
 * JSON cavab gözləyən sorğular — `httpStatus`-un üstündə nazik qatdır.
 *
 * ⚠ ÖZ TRANSPORTU YOXDUR (2026-08-01): əvvəl bu funksiyanın da ayrıca fetch/XHR
 *   cütü vardı və `httpStatus`-dakı ilə sürüşürdü. İndi şəbəkə davranışı — vaxt
 *   həddi, abort, origin bağlama — BİR yerdədir.
 */
export function httpJson<T>(url: string, init?: { method?: string; body?: string }): Promise<T> {
  return httpStatus(url, init).then((response) => {
    if (response.status < 200 || response.status >= 300) {
      throw new Error(`${url} → HTTP ${response.status}`)
    }
    try {
      return JSON.parse(response.text) as T
    } catch (error) {
      throw error instanceof Error ? error : new Error('JSON parse xətası')
    }
  })
}

/**
 * ⚠ `httpJsonAuth` BURADAN SİLİNDİ (2026-07-31). O, xam access token-i alıb
 *   `Bearer` kimi göndərirdi, AMMA token bitəndə YENİLƏMİRDİ — access token cəmi
 *   15 dəqiqəlikdir, ona görə `/api/me/profiles` səssizcə 401 alıb boş siyahı
 *   qaytarırdı və filmlər açılmırdı. Yerini `session.ts` → `authStatus()` tutdu:
 *   o, token-i lazım olanda yeniləyir, 401-də bir dəfə təkrarlayır və status kodunu
 *   saxlayır. AUTH-lu sorğu üçün BAŞQA yol yoxdur — xam token ilə sorğu yazma.
 */
